import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { BackrsUser } from '@backrs_ui/common-ui/src/models/user';

const LOGIN_ERROR_STRINGS = appStrings.modules.login.error.login;

export type RegistrationValues = Readonly<{
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  password: string;
  invite_code?: string | null;
  is_fourteen_plus: boolean;
  register_id: string | null;
  grade?: string;
  school?: string;
  zip_code?: string;
}>;

export function handleEmailLogin(email: string, password: string, environmentUrl: string) {
  return fetch(`${environmentUrl}/users/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, password }),
  });
}

export function handlePhoneLogin(phone: string, environmentUrl: string) {
  return fetch(`${environmentUrl}/otp/send-code`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone }),
  });
}

export function handleOtpVerify(code: string, phone: string, environmentUrl: string) {
  return fetch(`${environmentUrl}/otp/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code, phone }),
  });
}

export function handleBasicRegistration(payload: RegistrationValues, environmentUrl: string) {
  return fetch(`${environmentUrl}/users/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, accepted_terms: true }),
  });
}

export async function handleBasicRegistrationWithError(
  payload: RegistrationValues,
  environmentUrl: string,
): Promise<{
  status: number;
  token: string;
  user: BackrsUser;
}> {
  const response = await fetch(`${environmentUrl}/users/register`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...payload, accepted_terms: true }),
  });

  if (!response.ok) {
    const errorBody = await response.json();

    throw {
      status: response.status,
      response: errorBody, // <== this has your { errors: [...] }
    };
  }

  const responseJson = await response.json();

  return {
    status: response.status,
    token: responseJson.token,
    user: responseJson.user,
  };
}

export function sendPasswordReset(email: string, environmentUrl: string) {
  return fetch(`${environmentUrl}/users/send-password-reset-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  });
}

export function verifyTempToken(token: string, environmentUrl: string) {
  const decodedToken = decodeURIComponent(token);

  return fetch(`${environmentUrl}/users/crossdomain`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${decodedToken}`,
    },
  });
}

export const decodeToken = (tok: string) => {
  const base64Url = tok.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload);
};

export const getTokenExpirationDate = (tok: string) => new Date(decodeToken(tok).exp * 1000);

export const ENVIRONMENTS = {
  local: 'local',
  dev: 'development',
  staging: 'staging',
  stg: 'stg',
  prod: 'production',
  prd: 'prd',
};

export const computeEnvironment = (environment: string) => {
  const env = environment || ENVIRONMENTS.local;

  return env;
};

export async function handleAutoLogin(
  access_token: string,
  environmentUrl: string,
): Promise<BackrsUser | null> {
  const response = await fetch(`${environmentUrl}/users/auto-login`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${access_token}`,
      'Content-Type': 'application/json',
    },
  });

  if (response.ok) {
    const jsonUser = await response.json();

    const user: BackrsUser = {
      ...jsonUser,
      access_token,
      birthday: jsonUser.birthday
        ? new Date(jsonUser.birthday).toISOString().split('T')[0]
        : undefined,
    };

    return user;
  }

  return null;
}

type HandleLoggingInProps = Readonly<{
  phone: string;
  email: string;
  password: string;
  environmentUrl: string;
  afterLogin: (loginResponse: { access_token: string | undefined }) => Promise<void>;
  setErrorMessage: (error: string | undefined) => void;
}>;

export const handleLoggingIn = async ({
  phone,
  email,
  password,
  environmentUrl,
  afterLogin,
  setErrorMessage,
}: HandleLoggingInProps) => {
  const res = phone
    ? await handlePhoneLogin(phone, environmentUrl)
    : await handleEmailLogin(email, password, environmentUrl);

  if (!res.ok) {
    if (res.status === 503) {
      setErrorMessage('Service is currently unavailable. Please try again later.');
    }

    setErrorMessage(phone ? LOGIN_ERROR_STRINGS.phone : LOGIN_ERROR_STRINGS.userNameOrPassword);

    return;
  }

  const loginResponse = await res.json();
  const loginObject = !phone ? loginResponse : { ...loginResponse, phone };

  return afterLogin && (await afterLogin(loginObject));
};

type HandleUserRegistrationProps = Readonly<{
  payload: RegistrationValues;
  environmentUrl: string;
  afterRegistration: () => void;
  setErrorMessage: (error: string | undefined) => void;
}>;

export const handleUserRegistration = async ({
  payload,
  environmentUrl,
  afterRegistration,
  setErrorMessage,
}: HandleUserRegistrationProps) => {
  const res = await handleBasicRegistration(payload, environmentUrl);

  if (!res.ok) {
    setErrorMessage('Error registering user');

    return;
  }

  return afterRegistration && afterRegistration();
};

function computeDomain(hostname: string): string {
  const parts = hostname.split('.');
  const len = parts.length;

  // Check for .backrs.com (e.g., app.backrs.com)
  if (len >= 2 && parts[len - 2] === 'backrs' && parts[len - 1] === 'com') {
    return '.backrs.com';
  }

  // Check for *.backrs-dev.com (e.g., dev.backrs-dev.com, sub.stg.backrs-dev.com)
  if (len >= 3 && parts[len - 2] === 'backrs-dev' && parts[len - 1] === 'com') {
    const subDomain = parts[len - 3];

    // Ensure the subdomain is one of the known environments (dev, staging, stg, prd)
    // This prevents matching something like 'random.backrs-dev.com'
    if (['dev', 'staging', 'stg', 'prd'].includes(subDomain)) {
      return `.${subDomain}.backrs-dev.com`;
    }
  }

  // Default for localhost or other unknown hostnames
  return '.app.localhost';
}

export const setCookie = (token: string, environment: string) => {
  const env = computeEnvironment(environment);
  const expirationDate = getTokenExpirationDate(token);
  const secureTag = env !== 'local' ? '; secure' : '';

  document.cookie = `token=${encodeURIComponent(token)}; domain=${computeDomain(window.location.hostname)}; path=/; expires=${expirationDate.toUTCString()}${secureTag}`;
};

export const getCookie = () => {
  const cookie = document.cookie.split('; ').find((row) => row.startsWith('token='));

  if (!cookie) {
    return null;
  }

  const value = cookie.split('=')[1];

  return decodeURIComponent(value);
};

export const removeCookie = () => {
  document.cookie = `token=; path=/; domain=${computeDomain(window.location.hostname)}; expires=Thu, 01 Jan 1970 00:00:01 GMT;`;
};

type TokenType = 'backrs_ui_token';

export const getAccessToken = (tokenType: TokenType): string | null => {
  const accessToken = localStorage.getItem(tokenType);

  if (!accessToken) {
    return null;
  }

  return accessToken;
};

export const setAccessToken = (tokenType: TokenType, token: string): void => {
  localStorage.setItem(tokenType, token);
};

export const deleteAccessToken = (tokenType: TokenType): void => {
  localStorage.removeItem(tokenType);
};

export const getAccessTokenOrCookie = (
  tokenType: TokenType,
  isUnifiedLoginDisabled = false,
): string | null => {
  const cookie = getCookie();

  if (!cookie && !isUnifiedLoginDisabled) {
    return null;
  }

  return getAccessToken(tokenType) || cookie;
};
